import _objectSpread from "/home/runner/_work/mono/mono/node_modules/.pnpm/@babel+runtime@7.9.0/node_modules/@babel/runtime/helpers/esm/objectSpread2";
import _objectWithoutProperties from "/home/runner/_work/mono/mono/node_modules/.pnpm/@babel+runtime@7.9.0/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["invite", "sender", "team"];
import { translateObject, translateObjectInverse } from '@huggg/bar-staff';
import { path, prop } from 'ramda';
import { urls } from '../config';
import getCursorPaginationParams from '../utils/getCursorPaginationParams';
import withAxios from '../utils/with-axios';
import { cursorPaginatedResponseMap, teamMap, teamInviteMap, userMap } from '../translationMaps';
var DEFAULT_PER_PAGE = 20;
function formatTeam(data) {
  var team = data.data;
  var translated = translateObject(teamMap, team);
  return translated;
}
function formatTeamUsers(data) {
  var users = data.users;
  return users.map(function (user) {
    return translateObject(userMap, user);
  });
}
function formatTeamInvite(data) {
  var invite = data.data;
  return translateObject(teamInviteMap, invite);
}
function formatTeamInvites(data) {
  var translatedPagination = translateObject(cursorPaginatedResponseMap, data);
  if (translatedPagination.data.length) {
    translatedPagination.data = translatedPagination.data.map(function (item) {
      return formatTeamInvite({
        data: item
      });
    });
  }
  return translatedPagination;
}

/**
 * Fetch a singular team by ID
 *
 * @param {Object} $axios [$axios=axios] Axios instance.
 * @param {String} id The ID of the team
 * @returns {Promise} Unresolved Promise from the API.
 */
function single($axios, id) {
  if (!id) {
    throw new Error('Prop `id` is required in `teams.single`');
  }
  return $axios.get(urls.apiV2("/teams/".concat(id))).then(prop('data')).then(formatTeam);
}

/**
 * Fetches a team's users
 *
 * @param {Object} $axios [$axios=axios] Axios instance.
 * @param {String} id The ID of the team
 * @returns {Promise} Unresolved Promise from the API.
 */
function users($axios, id) {
  if (!id) {
    throw new Error('Prop `id` is required in `teams.users`');
  }
  return $axios.get(urls.apiV2("/teams/".concat(id, "/users"))).then(prop('data')).then(formatTeamUsers);
}

/**
 * Fetches a team's wallets
 *
 * @param {Object} $axios [$axios=axios] Axios instance.
 * @param {String} id The ID of the team
 * @returns {Promise} Unresolved Promise from the API.
 */
function wallets($axios, id) {
  if (!id) {
    throw new Error('Prop `id` is required in `teams.users`');
  }
  return $axios.get(urls.apiV2("/teams/".concat(id, "/wallets"))).then(path(['data', 'data']));
}

/**
 * Invites a new user/subteam to a team
 *
 * @param {Object} $axios [$axios=axios] Axios instance
 * @param {String} teamId The ID of the team
 * @param {Object} inviteObject The invite object
 * @param {String} inviteObject.type The type of invite: subteam or participant
 * @param {String} inviteObject.emailAddress The email address of the user
 * @returns {Promise} Unresolved Promise from the API
 */
function sendInvite($axios, teamId, inviteObject) {
  if (!teamId) {
    throw new Error('Prop `id` is required in `teams.sendInvite`');
  }
  return $axios.post(urls.apiV2("/teams/".concat(teamId, "/users/invite")), inviteObject).then(prop('data'));
}

/**
 * Lists all invites to a team
 *
 * @param {Object} $axios [$axios=axios] Axios instance
 * @param {String} teamId The ID of the team
 * @param {Object[]} options
 * @param {Number} [options[].usePagination=true] Whether to use pagination
 * @param {Number} [options[].limit=DEFAULT_PER_PAGE]
 *     How many items to  display per page
 * @param {String} [options[].next] Cursor to paginate forwards from
 * @param {String} [options[].previous] Cursor to paginate backwards from
 * @param {String} [options[].type] The type of invite to fetch (participant or subteam)
 *
 * @returns {Promise} Unresolved Promise from the API
 */
function invites($axios, teamId) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  if (!teamId) {
    throw new Error('Prop `id` is required in `teams.invites`');
  }
  var defaultParams = {
    use_pagination: true,
    limit: DEFAULT_PER_PAGE
  };
  var params = getCursorPaginationParams(options, defaultParams);
  return $axios.get(urls.apiV2("/teams/".concat(teamId, "/invites")), {
    params: params
  }).then(prop('data')).then(formatTeamInvites);
}

/**
 * Updates a team
 *
 * @param {Object} $axios [$axios=axios] Axios instance
 * @param {String} teamId The ID of the tea
 * @param {Object} update The (partial) update object
 * @returns {Promise} Unresolved Promise from the API
 */
function update($axios, teamId, data) {
  var formatted = translateObjectInverse(teamMap, data);
  return $axios.put(urls.apiV2("/teams/".concat(teamId)), formatted).then(prop('data')).then(formatTeam);
}

/**
 * Gets a team invite from the token
 *
 * @param {Object} $axios [$axios=axios] Axios instance
 * @param {String} token The token
 * @returns {Promise} Unresolved Promise from the API
 */
function getInvite($axios, token) {
  if (!token) {
    throw new Error('Prop `token` is required in `teams.getInvite`');
  }
  return $axios.get(urls.apiV2("/teams/invites/".concat(token))).then(prop('data')).then(function (data) {
    var invite = data.invite,
      sender = data.sender,
      team = data.team,
      rest = _objectWithoutProperties(data, _excluded);
    return _objectSpread({
      invite: translateObject(teamInviteMap, invite),
      sender: translateObject(userMap, sender),
      team: translateObject(teamMap, team)
    }, rest);
  });
}

/**
 * Accepts a team invite with a token
 *
 * @param {Object} $axios [$axios=axios] Axios instance
 * @param {String} token The token
 * @param {Object} data The associated data (if user is new) for user creation
 * @returns {Promise} Unresolved Promise from the API
 */
function acceptInvite($axios, token) {
  var data = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  if (!token) {
    throw new Error('Prop `token` is required in `teams.acceptInvite`');
  }
  var formatted = translateObjectInverse(_objectSpread(_objectSpread({}, userMap), {}, {
    password: 'password'
  }), data);
  return $axios.post(urls.apiV2("/teams/invites/".concat(token, "/accept")), formatted).then(prop('data')).then(function (invite) {
    return translateObject(teamInviteMap, invite);
  });
}

/**
 * Removes an invite from a team
 *
 * @param {Object} $axios [$axios=axios] Axios instance
 * @param {String} teamId The team ID
 * @param {String} inviteId The invite ID
 * @returns {Promise} Unresolved Promise from the API
 */
function removeInvite($axios, teamId, inviteId) {
  if (!teamId) {
    throw new Error('Prop `teamId` is required in `teams.removeInvite`');
  }
  if (!inviteId) {
    throw new Error('Prop `inviteId` is required in `teams.removeInvite`');
  }
  return $axios.delete(urls.apiV2("/teams/".concat(teamId, "/invites/").concat(inviteId))).then(prop('data'));
}

/**
 * Removes a user from a team
 *
 * @param {Object} $axios [$axios=axios] Axios instance
 * @param {String} teamId The team ID
 * @param {String} userId The user ID
 * @returns {Promise} Unresolved Promise from the API
 */
function removeUser($axios, teamId, userId) {
  if (!teamId) {
    throw new Error('Prop `teamId` is required in `teams.removeUser`');
  }
  if (!userId) {
    throw new Error('Prop `userId` is required in `teams.removeUser`');
  }
  return $axios.delete(urls.apiV2("/teams/".concat(teamId, "/users/").concat(userId))).then(prop('data'));
}

/**
 * Removes a subteam from a team
 *
 * @param {Object} $axios [$axios=axios] Axios instance
 * @param {String} teamId The team ID
 * @param {String} subteamId The subteam ID
 * @returns {Promise} Unresolved Promise from the API
 */
function removeSubteam($axios, teamId, subteamId) {
  if (!teamId) {
    throw new Error('Prop `teamId` is required in `teams.removeSubteam`');
  }
  if (!subteamId) {
    throw new Error('Prop `userId` is required in `teams.removeSubteam`');
  }
  return $axios.delete(urls.apiV2("/teams/".concat(teamId, "/subteams/").concat(subteamId))).then(prop('data'));
}
export default {
  acceptInvite: withAxios(acceptInvite),
  getInvite: withAxios(getInvite),
  invites: withAxios(invites),
  removeInvite: withAxios(removeInvite),
  removeSubteam: withAxios(removeSubteam),
  removeUser: withAxios(removeUser),
  sendInvite: withAxios(sendInvite),
  single: withAxios(single),
  update: withAxios(update),
  users: withAxios(users),
  wallets: withAxios(wallets)
};